/**
 * Budget-reset anomaly dashboard — query hooks + the control-gap CSV export URL.
 *
 * Tenant-scoped, READ-ONLY (observe-only): the detector is the only writer. Backed by the Item 3
 * read surface: GET /api/v1/tenants/{tenant_id}/agents/budget-reset-anomalies.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  anomalyListResponseSchema,
  type AnomalyListResponse,
  type AnomaliesSearch,
} from "./schemas";

const PAGE_SIZE = 50;
// A bounded read for the nav "needs attention" badge — anomalies are rare, so one page of the
// actionable (un-caught) set is plenty; the badge renders "N" (or "N+" at the cap).
const ACTIONABLE_SCAN_LIMIT = 100;

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agents/budget-reset-anomalies`;
}

// filterParams = the filters that define WHICH rows (not pagination). BOTH the paginated JSON fetch
// and the full CSV export consume this, so the export can never silently drift from the filtered
// view. carried_forward is a boolean → serialized as "true"/"false".
function filterParams(search: AnomaliesSearch): Record<string, string> {
  const p: Record<string, string> = {};
  if (search.signature_kind) p.signature_kind = search.signature_kind;
  if (search.confidence) p.confidence = search.confidence;
  if (search.carried_forward !== undefined) p.carried_forward = String(search.carried_forward);
  if (search.from) p.from = search.from;
  if (search.to) p.to = search.to;
  return p;
}

export async function fetchAnomalies(
  tenantID: string,
  search: AnomaliesSearch,
): Promise<AnomalyListResponse> {
  const searchParams: Record<string, string | number> = {
    ...filterParams(search),
    limit: PAGE_SIZE,
    offset: search.offset ?? 0,
  };
  const raw = await apiFetch<unknown>(base(tenantID), { searchParams });
  return anomalyListResponseSchema.parse(raw);
}

export function useAnomaliesQuery(tenantID: string, search: AnomaliesSearch) {
  const keyed = {
    signature_kind: search.signature_kind,
    confidence: search.confidence,
    carried_forward: search.carried_forward,
    offset: search.offset ?? 0,
  };
  return useQuery<AnomalyListResponse, ApiError>({
    queryKey: queryKeys.agents.budgetResetAnomalies(tenantID, keyed),
    queryFn: () => fetchAnomalies(tenantID, search),
    enabled: tenantID.length > 0,
    // Governance reporting, not a live feed. The detector sweeps on its own cadence; a slow poll
    // keeps the fleet view current without hammering an append-only audit surface.
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

/**
 * useActionableAnomalyCountQuery — the count of ACTIONABLE (un-caught) resets: carried_forward=false
 * (sever_via_delete / fresh_identity_suspected / standing_delegation_reset — the resets that went
 * silent). Drives the nav "needs attention" badge (the smallest-correct in-app PUSH: it turns the
 * headless detector into an operator-visible signal without any backend/notification change). The
 * benign caught rows (carried_forward=true) are deliberately excluded.
 */
export interface ActionableAnomalyCount {
  count: number;
  /** true when the scan hit ACTIONABLE_SCAN_LIMIT, so the badge should read "N+" not a bare N. */
  capped: boolean;
}

export function useActionableAnomalyCountQuery(tenantID: string, options?: { enabled?: boolean }) {
  return useQuery<ActionableAnomalyCount, ApiError>({
    queryKey: queryKeys.agents.budgetResetActionableCount(tenantID),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(base(tenantID), {
        searchParams: { carried_forward: "false", limit: ACTIONABLE_SCAN_LIMIT, offset: 0 },
      });
      const n = anomalyListResponseSchema.parse(raw).anomalies.length;
      return { count: n, capped: n >= ACTIONABLE_SCAN_LIMIT };
    },
    enabled: tenantID.length > 0 && (options?.enabled ?? true),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export const ANOMALIES_PAGE_SIZE = PAGE_SIZE;
export const ANOMALIES_ACTIONABLE_CAP = ACTIONABLE_SCAN_LIMIT;

/**
 * Build the control-gap CSV export URL — the auditor "prove-a-negative" artifact. The caller opens
 * it via <a href download> so the browser's native streaming download is used. No limit/offset: the
 * backend streams the FULL filtered set for format=csv (a compliance export must be complete). An
 * optional [from,to) date window scopes the artifact to the audit period.
 */
export function anomaliesExportURL(
  tenantID: string,
  search: AnomaliesSearch,
  range?: { from?: string; to?: string },
): string {
  const params = new URLSearchParams({ format: "csv", ...filterParams(search) });
  if (range?.from) params.set("from", range.from);
  if (range?.to) params.set("to", range.to);
  return `${base(tenantID)}?${params.toString()}`;
}
